import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../api/axiosInstance'; // Import axiosInstance
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  Collapse,
  List,
  ListItem,
  Button, // Added missing comma here
  useMediaQuery, // Added for responsiveness
  useTheme, // Added for responsiveness
} from '@mui/material';
import jsPDF from 'jspdf';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CanadaPRChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const [canAskMore, setCanAskMore] = useState(true);
  const [remainingQuestions, setRemainingQuestions] = useState(3);
  const [requiresAuth, setRequiresAuth] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check for small screens
  const shouldShowChatBot = !location.pathname.includes('/forums');

  // ——————————————————————————————————————————————
  // 1) Multi-page PDF export (jsPDF.html with scale=1)
  // ——————————————————————————————————————————————
  const handleDownloadPdf = () => {
    if (!messagesContainerRef.current) return;

    const pdf = new jsPDF('p', 'pt', 'a4');
    const margin = 10;
    pdf.html(messagesContainerRef.current, {
      x: margin,
      y: margin,
      html2canvas: {
        scale: 1,
        windowWidth: messagesContainerRef.current.scrollWidth,
        windowHeight: messagesContainerRef.current.scrollHeight
      },
      callback: doc => doc.save('trackcopr-chat.pdf'),
      margin: [margin, margin, margin, margin]
    });
  };

  // ——————————————————————————————————————————————
  // 2) Load saved messages & question counts
  // ——————————————————————————————————————————————
  useEffect(() => {
    const storedMsgs = localStorage.getItem('mapleGuideMessages');
    if (storedMsgs) {
      setMessages(JSON.parse(storedMsgs));
    } else {
      setMessages([{
        text: "Hello! I'm MapleGuide, your Canadian immigration assistant. How can I help with your PR questions today?",
        sender: 'bot'
      }]);
    }

    const count = parseInt(localStorage.getItem('mapleGuideQuestionsAsked') || '0', 10);
    setQuestionsAsked(count);

    if (user) {
      setCanAskMore(count < 5);
    } else {
      setRemainingQuestions(Math.max(0, 3 - count));
      setCanAskMore(count < 3);
      setRequiresAuth(count >= 3);
    }
  }, [user]);

  // ——————————————————————————————————————————————
  // 3) Persist messages
  // ——————————————————————————————————————————————
  useEffect(() => {
    if (messages.length) {
      localStorage.setItem('mapleGuideMessages', JSON.stringify(messages));
    }
  }, [messages]);

  // ——————————————————————————————————————————————
  // 4) Auto-scroll
  // ——————————————————————————————————————————————
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const toggleChat = () => setIsOpen(o => !o);

  const handleLogin = () => {
    // Use the baseURL from the axiosInstance for consistency, even for redirects
    const authUrl = `${axiosInstance.defaults.baseURL}/auth/google`;
    window.location.href = authUrl;
  };

  // ——————————————————————————————————————————————
  // 5) Send a new message
  // ——————————————————————————————————————————————
  const handleSendMessage = async e => {
    e.preventDefault();
    if (!newMessage.trim() || loading || (!canAskMore && user) || (requiresAuth && !user)) return;

    const userMsg = { text: newMessage.trim(), sender: 'user' };
    setMessages(ms => [...ms, userMsg]);
    setNewMessage('');
    setLoading(true);

    try {
      const payload = [...messages, userMsg].map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text
      }));

      // Use axiosInstance.post
      const response = await axiosInstance.post('/api/grok/chat', { messages: payload });

      // Axios handles JSON parsing, data is in response.data
      const data = response.data;
      const newCount = questionsAsked + 1;
      setQuestionsAsked(newCount);
      localStorage.setItem('mapleGuideQuestionsAsked', newCount.toString());

      if (!user) {
        const rem = Math.max(0, 3 - newCount);
        setRemainingQuestions(rem);
        if (rem <= 0) {
          setRequiresAuth(true);
          setCanAskMore(false);
        }
      } else if (newCount >= 5) {
        setCanAskMore(false);
      }

      const botReply = data.choices?.[0]?.message?.content;
      if (botReply) {
        setMessages(ms => [...ms, { text: botReply, sender: 'bot' }]);
        if ((user && newCount >= 5) || (!user && newCount >= 3)) {
          setMessages(ms => [
            ...ms,
            {
              text: user
                ? "You've reached your limit of 5 questions. Thanks for using MapleGuide!"
                : "You've reached the limit for guest users. Please log in to continue using MapleGuide!",
              sender: 'bot'
            }
          ]);
        }
      } else {
        // Handle cases where the API might return 200 OK but no valid reply
        console.warn("Received successful response but no valid bot reply:", data);
        setMessages(ms => [...ms, { text: "I received a response, but couldn't extract a reply. Please try rephrasing.", sender: 'bot' }]);
      }
    } catch (err) {
      console.error("Error sending message:", err);
      // Handle specific Axios error codes like 429 (Too Many Requests)
      if (err.response && err.response.status === 429) {
        setRequiresAuth(true);
        setCanAskMore(false);
        setMessages(ms => [
          ...ms,
          { text: "You've reached the limit for guest users. Please log in to continue using MapleGuide!", sender: 'bot' }
        ]);
      } else {
        // Generic error message
        const errorMessage = err.response?.data?.message || err.message || "I'm sorry, something went wrong. Please try again later.";
        setMessages(ms => [...ms, { text: errorMessage, sender: 'bot' }]);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!shouldShowChatBot) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: isMobile ? 10 : 20, // Adjust position for mobile
        right: isMobile ? 10 : 20,  // Adjust position for mobile
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        // Ensure it doesn't exceed viewport width on mobile
        width: isMobile ? 'calc(100% - 20px)' : 'auto', // Take most width on mobile, auto otherwise
        maxWidth: 360, // Keep max width for larger screens
      }}
    >
      {/* Adjust Collapse width and margin */}
      <Collapse in={isOpen} sx={{ width: '100%', mb: isMobile ? 1 : 2 }}>
        <Paper
          elevation={3}
          sx={{
            // Adjust height for mobile
            height: isMobile ? '70vh' : 480,
            maxHeight: isMobile ? 'calc(100vh - 80px)' : 480, // Ensure it fits viewport height
            width: '100%', // Ensure paper takes full width of Collapse
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            borderTop: '4px solid #D32F2F'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#D32F2F',
              color: '#FFFFFF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: isMobile ? '1.1rem' : '1.25rem' }}> {/* Slightly smaller title on mobile */}
              MapleGuide
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Hide PDF button on mobile to save space */}
              <Button
                size="small"
                onClick={handleDownloadPdf}
                sx={{
                  color: '#FFFFFF',
                  mr: 1,
                  display: isMobile ? 'none' : 'inline-flex' // Hide on mobile
                }}
              >
                DOWNLOAD PDF
              </Button>
              <IconButton size="small" onClick={toggleChat} sx={{ color: '#FFFFFF' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages container (ref’d) */}
          <Box
            ref={messagesContainerRef}
            sx={{ p: isMobile ? 1 : 2, flexGrow: 1, overflowY: 'auto', backgroundColor: '#FFFFFF' }} // Reduce padding on mobile
          >
            <List sx={{ py: 0 }}> {/* Remove default List padding */}
              {messages.map((msg, i) => (
                <ListItem
                  key={i}
                  sx={{
                    // avoid splitting a bubble across PDF pages:
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    display: 'flex',
                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    py: 0.5
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      maxWidth: '85%', // Allow slightly wider bubbles on mobile
                      backgroundColor: msg.sender === 'user' ? '#D32F2F' : '#F5F5F5', // Lighter background for bot
                      color: msg.sender === 'user' ? '#FFFFFF' : '#333333',
                      borderRadius:
                        msg.sender === 'user'
                          ? '18px 18px 4px 18px'
                          : '18px 18px 18px 4px'
                    }}
                  >
                    <Typography
                      variant="body2" // Keep body2 for consistency
                      sx={{
                        fontWeight: msg.sender === 'user' ? 500 : 400, // Slightly adjust weight
                        wordBreak: 'break-word' // Ensure long words wrap
                       }}
                    >
                      {msg.text}
                    </Typography>
                  </Paper>
                </ListItem>
              ))}

              {loading && (
                <ListItem
                  sx={{
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    py: 0.5
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      maxWidth: '75%',
                      backgroundColor: '#F5F5F5',
                      color: '#666',
                      borderRadius: '18px 18px 18px 4px',
                      fontStyle: 'italic',
                      '@keyframes blink': {
                        '0%': { content: '"…"' },
                        '33%': { content: '"."' },
                        '66%': { content: '".."' },
                        '100%': { content: '"…"' }
                      },
                      '& .MuiTypography-root::after': {
                        display: 'inline-block',
                        animation: `blink 1s steps(3) infinite`
                      }
                    }}
                  >
                    <Typography variant="body2">MapleGuide is typing</Typography>
                  </Paper>
                </ListItem>
              )}

              {requiresAuth && !user && (
                <ListItem sx={{ justifyContent: 'center', py: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={handleLogin}
                    sx={{ backgroundColor: '#D32F2F', color: '#FFFFFF', '&:hover': { backgroundColor: '#B71C1C' } }}
                  >
                    Log in to continue
                  </Button>
                </ListItem>
              )}

              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 1, borderTop: '1px solid #E0E0E0', backgroundColor: '#FFFFFF', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#D32F2F' }}>
              {user
                ? `${questionsAsked} of 5 questions used`
                : `${3 - remainingQuestions} of 3 free questions used${requiresAuth ? ' — please log in' : ''}`}
            </Typography>
          </Box>

          {/* Input */}
          {/* Input Area */}
          <Box component="form" onSubmit={handleSendMessage} sx={{ p: isMobile ? 1 : 2, backgroundColor: '#FAFAFA', display: 'flex', gap: 1, borderTop: '1px solid #E0E0E0' }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined" // Ensure consistent styling
              placeholder={
                user
                  ? canAskMore
                    ? "Ask a Canada PR question…"
                    : "Limit reached"
                  : requiresAuth
                    ? "Log in to continue"
                    : "Ask a Canada PR question…"
              }
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              disabled={(user && !canAskMore) || (!user && requiresAuth) || loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <IconButton
              type="submit"
              disabled={!newMessage.trim() || loading || (user && !canAskMore) || (!user && requiresAuth)}
              sx={{ color: '#D32F2F' }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>

      <Fab onClick={toggleChat} sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' } }}>
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>
    </Box>
  );
};

export default CanadaPRChatBot;
